/**
 * dealAnalytics — the derivations behind the deal page's charts.
 *
 * Kept out of the components on purpose. A chart should render what it is
 * given; deciding what "signed rent in 2023" means is an analytical choice and
 * it belongs somewhere it can be read, argued with and tested.
 *
 * Two rules run through all of it:
 *
 *   Nothing is invented. A period with no signings returns a null, which the
 *   chart draws as a gap. It is never interpolated, never carried forward and
 *   never replaced by the series average, because "we signed nothing that year"
 *   and "we signed at the average that year" are different facts.
 *
 *   Every function returns a `how` string. It is rendered verbatim behind the
 *   "how this is computed" control, so the reader can check the method rather
 *   than trusting the mark.
 */

/* ── small shared helpers ─────────────────────────────────────────── */
// Number(null) and Number("") are both 0, so a bare Number() check turns every
// empty field into a real zero. That is how "median time to LOI: 0 days across
// 613 deals" appeared on screen when four deals record one at all.
const num = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};
const ms = (d) => { const t = Date.parse(d); return Number.isFinite(t) ? t : null; };
export const yearOf = (d) => { const t = ms(d); return t ? new Date(t).getUTCFullYear() : null; };

/** Area-weighted mean. A 90,000 SF lease should not count as one vote. */
export function weightedMean(rows, val, wt) {
  let n = 0, d = 0;
  rows.forEach((r) => {
    const v = num(val(r)), w = num(wt(r));
    if (v != null && v > 0 && w != null && w > 0) { n += v * w; d += w; }
  });
  return d ? n / d : null;
}

export function median(values) {
  const v = values.filter((x) => Number.isFinite(x)).sort((a, b) => a - b);
  if (!v.length) return null;
  const m = Math.floor(v.length / 2);
  return v.length % 2 ? v[m] : (v[m - 1] + v[m]) / 2;
}

/** Ordinary least squares through (x, y), returned as two endpoints. */
export function linearTrend(points) {
  const p = points.filter((q) => Number.isFinite(q.x) && Number.isFinite(q.y));
  if (p.length < 3) return null;
  const n = p.length;
  const mx = p.reduce((s, q) => s + q.x, 0) / n;
  const my = p.reduce((s, q) => s + q.y, 0) / n;
  let sxy = 0, sxx = 0;
  p.forEach((q) => { sxy += (q.x - mx) * (q.y - my); sxx += (q.x - mx) ** 2; });
  if (!sxx) return null;
  const slope = sxy / sxx;
  const at = (x) => my + slope * (x - mx);
  const x0 = Math.min(...p.map((q) => q.x)), x1 = Math.max(...p.map((q) => q.x));
  const YEAR = 365.25 * 24 * 3600 * 1000;
  return {
    points: [{ x: x0, y: at(x0) }, { x: x1, y: at(x1) }],
    perYear: slope * YEAR,
    // Growth expressed against the fitted level at the start of the window.
    pctPerYear: at(x0) > 0 ? (slope * YEAR) / at(x0) : null,
    n,
  };
}

/* ── 1. signed rents over time ────────────────────────────────────── */
/**
 * What this building has signed, when it signed it, and what the submarket was
 * signing at the same moment.
 *
 * The property series is the area-weighted rent of leases that COMMENCED in
 * each period — not the rent roll as it stands. A rent roll is a snapshot of
 * decisions taken over a decade; plotting it by commencement is what turns it
 * into a history.
 */
export function signedRentHistory(rentRoll = [], comps = [], { bucket = "auto" } = {}) {
  const leases = rentRoll
    .filter((r) => !r.is_vacant)
    .map((r) => ({
      t: ms(r.lease_start), rent: num(r.lease_rate), sf: num(r.sf) || num(r.suite_sf),
      tenant: r.tenant, type: r.lease_type, end: r.lease_end,
    }))
    .filter((r) => r.t && r.rent > 0);

  const market = comps
    .map((c) => ({
      t: ms(c.comp_date), rent: num(c.lease_rate), sf: num(c.suite_sf),
      label: c.address, dist: num(c.distanceMiles),
    }))
    .filter((c) => c.t && c.rent > 0);

  if (!leases.length && !market.length) {
    return { empty: true, how: "No lease has a commencement date and a rent, and no dated comp is in range." };
  }

  const all = leases.concat(market);
  const span = (Math.max(...all.map((r) => r.t)) - Math.min(...all.map((r) => r.t))) / (365.25 * 864e5);
  const byYear = bucket === "year" || (bucket === "auto" && span >= 4);
  const key = (t) => {
    const d = new Date(t);
    return byYear ? `${d.getUTCFullYear()}` : `${d.getUTCFullYear()}Q${Math.floor(d.getUTCMonth() / 3) + 1}`;
  };
  const keyStart = (k) => (byYear
    ? Date.UTC(Number(k), 6, 1)
    : Date.UTC(Number(k.slice(0, 4)), (Number(k.slice(5)) - 1) * 3 + 1, 15));

  // Every period between the first and last observation exists, even the empty
  // ones — that is what makes a gap visible instead of invisible.
  const periods = [];
  const t0 = Math.min(...all.map((r) => r.t)), t1 = Math.max(...all.map((r) => r.t));
  const d0 = new Date(t0);
  let cur = byYear ? Date.UTC(d0.getUTCFullYear(), 0, 1)
                   : Date.UTC(d0.getUTCFullYear(), Math.floor(d0.getUTCMonth() / 3) * 3, 1);
  while (cur <= t1) {
    periods.push(key(cur));
    const c = new Date(cur);
    cur = byYear ? Date.UTC(c.getUTCFullYear() + 1, 0, 1)
                 : Date.UTC(c.getUTCFullYear(), c.getUTCMonth() + 3, 1);
  }

  const series = (rows) => periods.map((k) => {
    const inK = rows.filter((r) => key(r.t) === k);
    const v = weightedMean(inK, (r) => r.rent, (r) => r.sf || 1);
    return { x: keyStart(k), y: v, n: inK.length };
  });

  const propertyPts = series(leases);
  const marketPts = series(market);

  // The band is the middle of the comp set, not its full range: one $40 office
  // conversion should not make the submarket look like it spans $6 to $40.
  const band = periods.map((k) => {
    const rents = market.filter((r) => key(r.t) === k).map((r) => r.rent).sort((a, b) => a - b);
    if (rents.length < 4) return null;
    const q = (p) => rents[Math.min(rents.length - 1, Math.floor(p * rents.length))];
    return { x: keyStart(k), lo: q(0.25), hi: q(0.75) };
  }).filter(Boolean);

  const propTrend = linearTrend(leases.map((r) => ({ x: r.t, y: r.rent })));
  const mktTrend = linearTrend(market.map((r) => ({ x: r.t, y: r.rent })));

  const last = (pts) => [...pts].reverse().find((p) => Number.isFinite(p.y));
  const lastProp = last(propertyPts), lastMkt = last(marketPts);

  const scatter = leases.map((r) => ({
    x: r.t, y: r.rent, size: r.sf || 1, label: r.tenant || "Signed lease",
    tone: "rgba(233,53,41,0.45)", kind: "property",
  })).concat(market.map((c) => ({
    x: c.t, y: c.rent, size: c.sf || 1, label: c.label || "Comp",
    tone: "rgba(244,244,244,0.14)", kind: "market",
  })));

  return {
    empty: false,
    granularity: byYear ? "year" : "quarter",
    property: propertyPts, market: marketPts, band, scatter,
    propTrend, mktTrend,
    counts: { property: leases.length, market: market.length },
    latest: {
      property: lastProp ? lastProp.y : null,
      market: lastMkt ? lastMkt.y : null,
      spread: lastProp && lastMkt && lastMkt.y ? lastProp.y / lastMkt.y - 1 : null,
    },
    how: `Each ${byYear ? "year" : "quarter"} plots the area-weighted rent of leases that COMMENCED in it — `
      + `${leases.length} signing${leases.length === 1 ? "" : "s"} at this property with both a start date and a rate, `
      + `against ${market.length} dated lease comp${market.length === 1 ? "" : "s"} in the radius. `
      + `The shaded band is the 25th–75th percentile of comps in periods holding at least four of them. `
      + `Periods with no signing are drawn as gaps and are not interpolated. `
      + `Trend lines are ordinary least squares through the individual leases, not through the period averages.`,
  };
}

/* ── 2. occupancy over time ───────────────────────────────────────── */
/**
 * The building's own occupancy history and its forward run-off, from lease
 * start and end dates. Past is what happened; anything after today is what
 * happens if nothing renews — which is the useful half of the picture.
 */
export function occupancyCurve(rentRoll = [], totalRsf = null, { backYears = 5, fwdYears = 3 } = {}) {
  const leases = rentRoll
    .filter((r) => !r.is_vacant)
    .map((r) => ({ s: ms(r.lease_start), e: ms(r.lease_end), sf: num(r.sf) || num(r.suite_sf) || 0 }))
    .filter((r) => r.s && r.sf > 0);
  if (!leases.length) return { empty: true, how: "No lease has a commencement date and an area." };

  const withEnd = leases.filter((r) => r.e).length;
  const now = Date.now();
  const start = Date.UTC(new Date(now).getUTCFullYear() - backYears, 0, 1);
  const end = Date.UTC(new Date(now).getUTCFullYear() + fwdYears, 0, 1);

  const pts = [];
  let t = start;
  while (t <= end) {
    const at = t;   // bound per iteration: the closures below must not see the next month
    const live = leases.filter((l) => l.s <= at && (!l.e || l.e >= at));
    const sf = live.reduce((s, l) => s + l.sf, 0);
    pts.push({ x: at, y: totalRsf ? sf / totalRsf : sf, n: live.length });
    const d = new Date(at);
    t = Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 1);
  }

  const today = pts.reduce((a, p) => (Math.abs(p.x - now) < Math.abs(a.x - now) ? p : a));
  const future = pts.filter((p) => p.x > now);
  const trough = future.length ? future.reduce((a, p) => (p.y < a.y ? p : a)) : null;

  return {
    empty: false, points: pts, todayX: now, today: today.y,
    trough: trough ? { x: trough.x, y: trough.y } : null,
    asPct: !!totalRsf,
    how: `Occupied area is recomputed at the first of every month from each lease's own start and end dates — `
      + `${leases.length} lease${leases.length === 1 ? "" : "s"} carry a start, ${withEnd} carry an end. `
      + `A lease with no end date is treated as still running, so the forward line is the FLOOR of what rolls off, not a forecast. `
      + (totalRsf ? `Divided by ${Math.round(totalRsf).toLocaleString()} SF of building area.` : `Shown in square feet — no building area is held for a percentage.`)
      + ` Nothing after today assumes a renewal: that is the point of the line.`,
  };
}

/* ── 3. rollover profile ──────────────────────────────────────────── */
export function rolloverProfile(rentRoll = [], totalRsf = null, years = 8) {
  const thisYear = new Date().getFullYear();
  const rows = rentRoll.filter((r) => !r.is_vacant && r.lease_end);
  if (!rows.length) return { empty: true, how: "No lease end dates are held." };

  const out = [];
  let cum = 0;
  for (let i = 0; i < years; i += 1) {
    const y = thisYear + i;
    const inY = rows.filter((r) => yearOf(r.lease_end) === y);
    const sf = inY.reduce((s, r) => s + (num(r.sf) || num(r.suite_sf) || 0), 0);
    const rent = weightedMean(inY, (r) => r.lease_rate, (r) => num(r.sf) || 1);
    cum += sf;
    out.push({
      year: y, sf, count: inY.length, rent,
      share: totalRsf ? sf / totalRsf : null,
      cumShare: totalRsf ? cum / totalRsf : null,
      tenants: inY.map((r) => r.tenant).filter(Boolean),
    });
  }
  const beyond = rows.filter((r) => (yearOf(r.lease_end) || 0) >= thisYear + years).length;
  return {
    empty: false, rows: out, beyond,
    how: `Each column is the area of leases whose stated end date falls in that year, from ${rows.length} lease${rows.length === 1 ? "" : "s"} carrying one. `
      + `The dashed line is the cumulative share of the building rolled by that year. `
      + `Options are not exercised and renewals are not assumed — this is the contractual position.`,
  };
}

/* ── 4. in-place against underwritten expenses ────────────────────── */
const BASIS_ORDER = ["T12", "YTD", "UW In-Place", "OM Pro Forma", "Supporting"];
const BASIS_MEANING = {
  "T12": "what the property actually spent, trailing twelve months",
  "YTD": "what the property has spent this year to date",
  "UW In-Place": "what we underwrote",
  "OM Pro Forma": "what the broker showed",
  "Supporting": "a supporting schedule from the file",
};

// Which basis stands in for "what it really costs", best evidence first. Only
// seven deals in the whole database hold both a T12 and our own schedule, so a
// comparison that insists on a T12 is a comparison that almost never runs. The
// weaker partners are still worth reading against - they are simply a different
// claim, and `comparisonKind` below makes the component say which.
const ACTUAL_PREFERENCE = ["T12", "YTD", "Supporting", "OM Pro Forma"];
const COMPARISON_KIND = {
  "T12": "actual", "YTD": "actual",
  "Supporting": "schedule", "OM Pro Forma": "broker",
};

/**
 * Expense categories against the bases they were stated on, as a heatmap, plus
 * the line-by-line variance between what the property spends and what we
 * underwrote — the comparison that decides whether the pro forma is honest.
 */
export function opexCompare(opexLines = [], totalRsf = null) {
  const lines = opexLines.filter((r) => !r.is_subtotal && r.category !== "Total");
  if (!lines.length) return { empty: true, how: "No expense line reconciled to a stated total, so none was loaded." };

  const bases = [...new Set(lines.map((r) => r.basis).filter(Boolean))]
    .sort((a, b) => {
      const ia = BASIS_ORDER.indexOf(a), ib = BASIS_ORDER.indexOf(b);
      return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
    });

  const cell = {};
  lines.forEach((r) => {
    const k = `${r.category}||${r.basis}`;
    cell[k] = (cell[k] || 0) + (num(r.amount_annual) || 0);
  });

  // A category that carries no amount on ANY basis is a label the extraction
  // saw and could not value. Showing it as a row of dashes suggests we have
  // eight categories when we have five, so it is dropped from the grid and
  // reported separately.
  const allCats = [...new Set(lines.map((r) => r.category).filter(Boolean))].sort();
  const categories = allCats.filter((c) => bases.some((b) => cell[`${c}||${b}`]));
  const unvalued = allCats.filter((c) => !categories.includes(c));

  const psf = (v) => (totalRsf && Number.isFinite(v) ? v / totalRsf : null);
  const cells = [];
  categories.forEach((c) => bases.forEach((b) => {
    const v = cell[`${c}||${b}`];
    if (!Number.isFinite(v) || !v) return;
    cells.push({ row: c, col: b, value: totalRsf ? v / totalRsf : v, n: 0, note: `${c} on the ${b} basis` });
  }));

  const totals = {};
  bases.forEach((b) => { totals[b] = categories.reduce((s, c) => s + (cell[`${c}||${b}`] || 0), 0); });

  // The comparison Daniel asks for by name: actual against underwritten.
  const uwBasis = bases.find((b) => b === "UW In-Place") || bases.find((b) => /uw|underwrit/i.test(b));
  const inPlaceBasis = ACTUAL_PREFERENCE.find((pref) => bases.includes(pref) && pref !== uwBasis)
    || bases.find((b) => b !== uwBasis && /t-?12|actual|in.?place|ytd/i.test(b));
  let variance = null;
  if (inPlaceBasis && uwBasis && inPlaceBasis !== uwBasis) {
    const rows = categories.map((c) => {
      const a = cell[`${c}||${inPlaceBasis}`] || null, u = cell[`${c}||${uwBasis}`] || null;
      return {
        category: c, inPlace: a, uw: u,
        inPlacePsf: psf(a), uwPsf: psf(u),
        delta: a && u ? u - a : null,
        deltaPct: a && u ? (u - a) / a : null,
        onlyIn: a && !u ? inPlaceBasis : u && !a ? uwBasis : null,
      };
    }).filter((r) => r.inPlace || r.uw)
      .sort((a, b) => Math.abs(b.delta || 0) - Math.abs(a.delta || 0));
    const tIn = totals[inPlaceBasis], tUw = totals[uwBasis];
    variance = {
      inPlaceBasis, uwBasis, rows,
      kind: COMPARISON_KIND[inPlaceBasis] || "schedule",
      inPlaceTotal: tIn, uwTotal: tUw,
      inPlacePsf: psf(tIn), uwPsf: psf(tUw),
      deltaPct: tIn ? (tUw - tIn) / tIn : null,
      missedCount: rows.filter((r) => r.onlyIn === inPlaceBasis).length,
    };
  }

  return {
    empty: false, bases, categories, cells, totals, unvalued,
    totalsPsf: Object.fromEntries(bases.map((b) => [b, psf(totals[b])])),
    meaning: BASIS_MEANING, variance, perSf: !!totalRsf,
    how: `Every expense line is summed by category and by the basis it was stated on, then divided by `
      + (totalRsf ? `${Math.round(totalRsf).toLocaleString()} SF of building area. ` : `nothing — no building area is held, so these are annual dollars. `)
      + `Bases are never blended: a T12 line and an underwritten line for the same category are two different claims and are kept apart. `
      + `Stated totals and the lines they total are never added together. `
      + (unvalued.length ? `${unvalued.length} category label${unvalued.length === 1 ? " was" : "s were"} read but carried no amount on any basis (${unvalued.join(", ")}) and ${unvalued.length === 1 ? "is" : "are"} left out of the grid rather than shown as a row of dashes. ` : ``)
      + (variance
        ? `The variance table reads ${variance.uwBasis} against ${variance.inPlaceBasis}: positive means we underwrote MORE expense than that basis shows. `
        + (variance.kind === "actual"
          ? `${variance.inPlaceBasis} is an actual, so this is the comparison that matters most.`
          : variance.kind === "broker"
            ? `No trailing-twelve actual is held for this deal, so the comparison is against the BROKER'S pro forma — a forward-looking claim, not a record of spending. Read it as a disagreement between two forecasts.`
            : `No trailing-twelve actual is held for this deal, so the comparison is against a supporting schedule from the file rather than against audited spending.`)
        : `Only one basis is held, so no in-place against underwritten comparison is possible.`),
  };
}

/* ── 5. expenses around the property ──────────────────────────────── */
/**
 * What expenses run at, near here. Two readings of the same evidence:
 *
 *   location   a compass grid centred on the subject, so the geography is
 *              legible — cheap to the north, dear to the east, and so on
 *   structure  distance ring against building size, which is usually the
 *              stronger explanation of why one property's expenses differ
 *
 * A cell with no comps is empty, never zero.
 */
export function areaOpexHeat(subject = {}, comps = [], radiusMiles = 5) {
  const lat = num(subject.latitude), lng = num(subject.longitude);
  const evidence = comps
    .map((c) => ({
      opex: num(c.opex_psf), lat: num(c.latitude), lng: num(c.longitude),
      sf: num(c.building_sf) || num(c.suite_sf), dist: num(c.distanceMiles),
      address: c.address,
    }))
    .filter((c) => c.opex > 0 && c.opex < 15);

  if (!evidence.length || lat == null || lng == null) {
    return { empty: true, how: "No property within the radius carries a stated expense figure, so there is nothing to shade." };
  }

  /* location grid ------------------------------------------------- */
  const third = radiusMiles / 3;
  const MPD_LAT = 69.0, mpdLng = 69.0 * Math.cos((lat * Math.PI) / 180) || 69.0;
  const bandOf = (delta, perMile) => {
    const mi = delta * perMile;
    return mi > third ? 0 : mi < -third ? 2 : 1;   // 0 = north/east side
  };
  const rowLabels = [`N of subject`, `within ${third.toFixed(1)} mi`, `S of subject`];
  const colLabels = [`W`, `centre`, `E`];
  const buckets = {};
  evidence.forEach((c) => {
    const r = bandOf(c.lat - lat, MPD_LAT);
    const cIdx = 2 - bandOf(c.lng - lng, mpdLng);   // east on the right
    const k = `${r}|${cIdx}`;
    (buckets[k] = buckets[k] || []).push(c.opex);
  });
  const locCells = Object.entries(buckets).map(([k, vals]) => {
    const [r, c] = k.split("|").map(Number);
    return {
      row: rowLabels[r], col: colLabels[c], value: median(vals), n: vals.length,
      note: `median of ${vals.length} stated expense figure${vals.length === 1 ? "" : "s"}`,
    };
  });

  /* structure grid ------------------------------------------------ */
  const rings = [[0, 1, "≤1 mi"], [1, 2, "1–2 mi"], [2, 3, "2–3 mi"], [3, radiusMiles, `3–${radiusMiles} mi`]];
  const sizes = [[0, 20000, "<20k SF"], [20000, 50000, "20–50k"], [50000, 100000, "50–100k"], [100000, Infinity, "100k+"]];
  const structCells = [];
  rings.forEach(([r0, r1, rl]) => sizes.forEach(([s0, s1, sl]) => {
    const inCell = evidence.filter((c) => c.dist != null && c.dist >= r0 && c.dist < r1 && c.sf >= s0 && c.sf < s1);
    if (!inCell.length) return;
    structCells.push({
      row: rl, col: sl, value: median(inCell.map((c) => c.opex)), n: inCell.length,
      note: `median of ${inCell.length}`,
    });
  }));

  const all = evidence.map((c) => c.opex);
  const sorted = all.slice().sort((a, b) => a - b);
  const pctile = (v) => (v == null ? null : sorted.filter((x) => x <= v).length / sorted.length);

  return {
    empty: false,
    location: { rows: rowLabels, cols: colLabels, cells: locCells },
    structure: { rows: rings.map((r) => r[2]), cols: sizes.map((s) => s[2]), cells: structCells },
    stats: {
      n: evidence.length, median: median(all),
      p25: sorted[Math.floor(sorted.length * 0.25)], p75: sorted[Math.floor(sorted.length * 0.75)],
      lo: sorted[0], hi: sorted[sorted.length - 1],
    },
    pctile,
    how: `Every property within ${radiusMiles} miles that carries a stated operating expense figure — ${evidence.length} of them — `
      + `is placed in a cell and the cell is shaded by the MEDIAN of what sits in it, with the count shown beneath. `
      + `The median rather than the mean, because one mis-stated triple-net figure should not repaint a cell. `
      + `Figures at or above $15/SF are refused as mis-parsed rather than plotted. `
      + `A cell with no evidence is left empty: it is not a zero and it is not the middle of the scale.`,
  };
}

/* ── 6. mark to market, as a percentage ───────────────────────────── */
/**
 * Daniel's correction, and he is right: an annual dollar figure is not
 * comparable between a 20,000 SF building and a 400,000 SF one. The headline
 * is the percentage the in-place rent has to move to reach market; the dollars
 * are the consequence and belong underneath it.
 */
export function markToMarket(rentRoll = [], marketRent = [], { impliedMarket = null } = {}) {
  const occupied = rentRoll.filter((r) => !r.is_vacant);
  const inPlace = weightedMean(occupied, (r) => r.lease_rate, (r) => num(r.sf) || num(r.suite_sf) || 1);
  const leasedSf = occupied.reduce((s, r) => s + (num(r.sf) || num(r.suite_sf) || 0), 0);

  // Per-suite market rents first (they are specific), the deal-level MLA next,
  // the comp-implied figure last — evidence beats assumption beats inference.
  const suiteMarket = weightedMean(
    occupied.map((r) => {
      const mr = marketRent.find((m) => m.unit_id && r.unit_id && m.unit_id === r.unit_id);
      return { m: num(mr?.rent_psf) ?? num(r.market_rent_new), sf: num(r.sf) || num(r.suite_sf) || 1 };
    }).filter((r) => r.m > 0),
    (r) => r.m, (r) => r.sf,
  );
  const dealMarket = (() => {
    const vals = marketRent.map((m) => num(m.rent_psf)).filter((v) => v > 0);
    return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
  })();

  const market = suiteMarket || dealMarket || impliedMarket || null;
  const source = suiteMarket ? "per-suite market rents"
    : dealMarket ? "the deal's market rent assumption"
    : impliedMarket ? "rent implied by nearby comps" : null;

  const pct = inPlace && market ? market / inPlace - 1 : null;
  return {
    inPlace, market, source, pct,
    annual: inPlace && market && leasedSf ? (market - inPlace) * leasedSf : null,
    leasedSf, leases: occupied.length,
    how: `In-place rent is the area-weighted rate across ${occupied.length} occupied suite${occupied.length === 1 ? "" : "s"} `
      + `(${Math.round(leasedSf).toLocaleString()} SF leased). Market is taken from ${source || "nothing held"}, `
      + `preferring per-suite figures over a deal-level assumption and an assumption over anything inferred from comps. `
      + `The percentage is market ÷ in-place − 1: how far the rent roll has to travel, which is comparable between buildings in a way `
      + `an annual dollar figure is not. The dollars beneath it apply that gap to leased area only — vacant suites are upside on lease-up, not mark to market.`,
  };
}

/* ── 7. rent against size, the shape of a submarket ───────────────── */
/** Small suites rent dearer. Worth seeing before arguing a comp is off. */
export function rentBySize(comps = [], subject = {}) {
  const pts = comps
    .map((c) => ({ sf: num(c.suite_sf), rent: num(c.lease_rate), label: c.address, dist: num(c.distanceMiles) }))
    .filter((c) => c.sf > 0 && c.rent > 0);
  if (pts.length < 4) return { empty: true, how: `Only ${pts.length} comps carry both an area and a rate — too few to read a shape into.` };
  const bands = [[0, 5000, "<5k"], [5000, 15000, "5–15k"], [15000, 40000, "15–40k"], [40000, 100000, "40–100k"], [100000, Infinity, "100k+"]];
  const rows = bands.map(([lo, hi, label]) => {
    const inB = pts.filter((p) => p.sf >= lo && p.sf < hi);
    return { label, value: weightedMean(inB, (p) => p.rent, (p) => p.sf), n: inB.length };
  }).filter((r) => r.n > 0);
  const subjectSf = num(subject.square_footage) || num(subject.building_sf);
  const subjectBand = subjectSf ? bands.find(([lo, hi]) => subjectSf >= lo && subjectSf < hi) : null;
  return {
    empty: false, rows,
    subjectBand: subjectBand ? subjectBand[2] : null,
    how: `Dated and undated lease comps in the radius, area-weighted within each size band. `
      + `${pts.length} comps carry both a suite area and a rate. `
      + `The band holding the subject is picked out in red.`,
  };
}
